import { useEventLogging } from '@AppModels/Logging';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getEnabledIntegrationDisplayNames } from '../../../../models/utils/integrationUtils';
import { useGetExternalIntegrationsQuery } from '../../../../queries/Integrations/useGetExternalIntegrations';
import { AppImportContactsViewModel, IBulkContactsRequest } from '../../../../viewmodels/AppViewModels';
import { ContactsSelfImportMapping } from '../ContactsSelfImportMapping';
import { ContactsSelfImportProcessing } from '../ContactsSelfImportProcessing';
import { ContactsSelfImportUpload } from '../ContactsSelfImportUpload';
import { styleSheet } from './styles';

interface Props {
	onRequestClose?: (filter?: IBulkContactsRequest) => void;
	completedBtnText?: string;
}

function _ContactsSelfImport({ onRequestClose, completedBtnText }: Props) {
	const { logPageView } = useEventLogging();
	const userSession = useUserSession();
	const [importContacts] = React.useState(() => new AppImportContactsViewModel(userSession));
	const [step, setStep] = React.useState<'upload' | 'map' | 'processing'>('upload');

	// Track if we've shown the merge warning toast (prevents showing multiple times)
	const hasShownMergeWarningRef = React.useRef(false);

	// Get V2 integrations
	const externalIntegrationsQuery = useGetExternalIntegrationsQuery({});

	// Get account data
	const allowEmailMergeIntoIntegration = userSession.account?.preferences?.allowEmailMergeIntoIntegration;
	const v1Integrations = userSession.account?.integrations;

	React.useEffect(() => {
		logPageView('ContactsSelfImport');
	}, [logPageView]);

	// Show merge warning toast when entering map step
	React.useEffect(() => {
		if (step !== 'map') return;
		if (hasShownMergeWarningRef.current) return;
		if (!allowEmailMergeIntoIntegration) return;
		// Wait for V2 integrations query to finish loading before showing toast
		if (externalIntegrationsQuery.isLoading) return;

		const enabledIntegrationNames = getEnabledIntegrationDisplayNames(v1Integrations, externalIntegrationsQuery.data);
		if (enabledIntegrationNames.length === 0) return;

		hasShownMergeWarningRef.current = true;

		// Format integration names naturally
		const integrationText =
			enabledIntegrationNames.length === 1
				? enabledIntegrationNames[0]
				: enabledIntegrationNames.length === 2
					? `${enabledIntegrationNames[0]} and ${enabledIntegrationNames[1]}`
					: `${enabledIntegrationNames.slice(0, -1).join(', ')}, and ${enabledIntegrationNames[enabledIntegrationNames.length - 1]}`;

		toast.warning(<strong>Integration Merge Notice</strong>, {
			description: (
				<div>
					Note: Based on your account settings, any contacts that you import with matching email addresses will
					automatically be merged with the contacts from your {integrationText} integrations.
				</div>
			),
			duration: 60000, // 60 seconds
			closeButton: true,
			style: {
				minWidth: '600px',
				left: '50%',
				transform: 'translateX(-50%)',
			},
		});
	}, [
		step,
		allowEmailMergeIntoIntegration,
		v1Integrations,
		externalIntegrationsQuery.data,
		externalIntegrationsQuery.isLoading,
	]);

	return (
		<div className={`${css(styleSheet.container, step === 'upload' ? styleSheet.containerVariableHeight : null)}`}>
			{step === 'upload' ? (
				<ContactsSelfImportUpload
					className={css(styleSheet.content, styleSheet.uploadContent)}
					importContacts={importContacts}
					onNextButtonClicked={() => setStep('map')}
				/>
			) : null}
			{step === 'map' ? (
				<ContactsSelfImportMapping
					className={css(styleSheet.content, styleSheet.mapContent)}
					importContacts={importContacts}
					onImportClicked={() => setStep('processing')}
					showMergeByNameCheckbox={true}
				/>
			) : null}
			{step === 'processing' ? (
				<ContactsSelfImportProcessing
					className={css(styleSheet.content, styleSheet.importContent)}
					importContacts={importContacts}
					onRequestClose={onRequestClose}
					completedBtnText={completedBtnText}
				/>
			) : null}
		</div>
	);
}

export const ContactsSelfImport = observer(_ContactsSelfImport);
